import { z } from 'zod';
/**
 * Schéma Zod RÉCURSIF de l'AST (via `z.lazy`) — garde-fou de bornes : `rating` entier
 * 0..5, enums `type`/`status`/`op`, `and`/`or` à enfants non vides, valeurs de `tag`/
 * `filename` non vides (`folder` peut être vide = racine). N'est JAMAIS exposé comme
 * JSON-schema de route (l'AST transite en TEXTE via `expr`), donc plus riche qu'un
 * schéma de corps de route sans souci du piège `z.toJSONSchema`.
 */
export const filterNodeSchema = z.lazy(() => z.union([
    z.object({ kind: z.literal('and'), children: z.array(filterNodeSchema).min(1) }),
    z.object({ kind: z.literal('or'), children: z.array(filterNodeSchema).min(1) }),
    z.object({ kind: z.literal('not'), child: filterNodeSchema }),
    z.object({ kind: z.literal('tag'), value: z.string().min(1) }),
    z.object({
        kind: z.literal('type'),
        value: z.enum(['image', 'animatedImage', 'video', 'videoWithSound']),
    }),
    z.object({
        kind: z.literal('rating'),
        op: z.enum(['gte', 'lte', 'eq']),
        value: z.number().int().min(0).max(5),
    }),
    z.object({ kind: z.literal('folder'), value: z.string() }),
    z.object({ kind: z.literal('filename'), value: z.string().min(1) }),
    z.object({ kind: z.literal('status'), value: z.enum(['tagged', 'untagged', 'aiPending']) }),
]));
/* ------------------------------------------------------------------------- *
 * Interne : normalisation, alias, tables de correspondance.
 * ------------------------------------------------------------------------- */
/** Repli insensible à la CASSE et aux ACCENTS (NFD + suppression des diacritiques + minuscules). */
const fold = (s) => s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
/** Alias multilingues des opérateurs (déjà repliés). */
const OR_WORDS = new Set(['ou', 'or', 'oder']);
const AND_WORDS = new Set(['et', 'and', 'und']);
// `fold` ne décompose pas `ß` (NFD le laisse tel quel) : la graphie allemande
// naturelle `außer` doit donc être listée EN PLUS de `ausser`.
const EXCEPT_WORDS = new Set(['sauf', 'non', 'not', 'nicht', 'ausser', 'außer']);
const KEYWORDS = new Set([...OR_WORDS, ...AND_WORDS, ...EXCEPT_WORDS]);
/** Jetons de `type:` repliés → valeur d'AST. */
const TYPE_TOKENS = new Map([
    ['image', 'image'],
    ['animee', 'animatedImage'],
    ['animated', 'animatedImage'],
    ['video', 'video'],
    ['video-son', 'videoWithSound'],
    ['son', 'videoWithSound'],
]);
/** Jetons de `statut:` repliés → valeur d'AST. */
const STATUS_TOKENS = new Map([
    ['taguee', 'tagged'],
    ['tagged', 'tagged'],
    ['non-taguee', 'untagged'],
    ['untagged', 'untagged'],
    ['a-valider', 'aiPending'],
    ['aipending', 'aiPending'],
]);
/** Erreur interne du parser (convertie en `ParseError` au sommet). NON exportée. */
class FilterParseError extends Error {
    code;
    pos;
    constructor(code, pos) {
        super(code);
        this.name = 'FilterParseError';
        this.code = code;
        this.pos = pos;
    }
}
/** Découpe le texte en jetons bruts. Les `(`/`)` sont structurels HORS guillemets ; à
 * l'intérieur de `"..."`, espaces et parenthèses font partie de la valeur. */
const lex = (input) => {
    const tokens = [];
    const n = input.length;
    let i = 0;
    while (i < n) {
        const c = input.charAt(i);
        if (/\s/.test(c)) {
            i++;
            continue;
        }
        if (c === '(') {
            tokens.push({ type: 'lparen', pos: i });
            i++;
            continue;
        }
        if (c === ')') {
            tokens.push({ type: 'rparen', pos: i });
            i++;
            continue;
        }
        const start = i;
        let text = '';
        const mask = [];
        let hadQuote = false;
        while (i < n) {
            const ch = input.charAt(i);
            if (ch === '"') {
                hadQuote = true;
                i++;
                while (i < n && input.charAt(i) !== '"') {
                    text += input.charAt(i);
                    mask.push(true);
                    i++;
                }
                if (i >= n)
                    throw new FilterParseError('unclosed_quote', start);
                i++;
                continue;
            }
            if (/\s/.test(ch) || ch === '(' || ch === ')')
                break;
            text += ch;
            mask.push(false);
            i++;
        }
        tokens.push({ type: 'word', pos: start, text, mask, hadQuote });
    }
    return tokens;
};
const splitField = (text, mask) => {
    for (let p = 0; p < text.length; p++) {
        if (mask[p] === true)
            continue;
        const c = text.charAt(p);
        if (c === ':')
            return { field: text.slice(0, p), op: ':', value: text.slice(p + 1) };
        if (c === '=')
            return { field: text.slice(0, p), op: '=', value: text.slice(p + 1) };
        if (c === '>' && text.charAt(p + 1) === '=' && mask[p + 1] !== true) {
            return { field: text.slice(0, p), op: '>=', value: text.slice(p + 2) };
        }
        if (c === '<' && text.charAt(p + 1) === '=' && mask[p + 1] !== true) {
            return { field: text.slice(0, p), op: '<=', value: text.slice(p + 2) };
        }
    }
    return null;
};
/** Résout un mot en NŒUD FEUILLE (peut lever `FilterParseError` avec la position du jeton). */
const makeLeaf = (text, pos, sf) => {
    if (sf === null) {
        if (text.length === 0)
            throw new FilterParseError('empty_value', pos);
        return { kind: 'tag', value: text };
    }
    const field = fold(sf.field);
    const raw = sf.value;
    if (field === 'tag') {
        if (raw.length === 0)
            throw new FilterParseError('empty_value', pos);
        return { kind: 'tag', value: raw };
    }
    if (field === 'type') {
        const t = TYPE_TOKENS.get(fold(raw));
        if (t === undefined)
            throw new FilterParseError('unknown_type', pos);
        return { kind: 'type', value: t };
    }
    if (field === 'note') {
        const op = sf.op === '>=' ? 'gte' : sf.op === '<=' ? 'lte' : 'eq';
        if (!/^\d+$/.test(raw))
            throw new FilterParseError('invalid_rating', pos);
        const value = Number(raw);
        if (value > 5)
            throw new FilterParseError('invalid_rating', pos);
        return { kind: 'rating', op, value };
    }
    // `ordner` : alias ALLEMAND. `name`/`status` couvrent déjà l'allemand par
    // homographie avec l'anglais — `ordner` était le seul champ sans équivalent, ce
    // qui obligeait la légende allemande à rester partiellement en français.
    if (field === 'dossier' || field === 'folder' || field === 'ordner') {
        return { kind: 'folder', value: raw };
    }
    if (field === 'nom' || field === 'name') {
        if (raw.length === 0)
            throw new FilterParseError('empty_value', pos);
        return { kind: 'filename', value: raw };
    }
    if (field === 'statut' || field === 'status') {
        const s = STATUS_TOKENS.get(fold(raw));
        if (s === undefined)
            throw new FilterParseError('unknown_status', pos);
        return { kind: 'status', value: s };
    }
    throw new FilterParseError('unknown_field', pos);
};
/** Classe chaque jeton brut : opérateur (mot nu réservé) ou feuille (résolue via `makeLeaf`). */
const classify = (raw) => raw.map((r) => {
    if (r.type === 'lparen')
        return { t: 'lparen', pos: r.pos };
    if (r.type === 'rparen')
        return { t: 'rparen', pos: r.pos };
    const sf = splitField(r.text, r.mask);
    if (!r.hadQuote && sf === null) {
        const f = fold(r.text);
        if (OR_WORDS.has(f))
            return { t: 'or', pos: r.pos };
        if (AND_WORDS.has(f))
            return { t: 'and', pos: r.pos };
        if (EXCEPT_WORDS.has(f))
            return { t: 'except', pos: r.pos };
    }
    return { t: 'leaf', pos: r.pos, node: makeLeaf(r.text, r.pos, sf) };
});
/* ------------------------------------------------------------------------- *
 * API publique : parsing.
 * ------------------------------------------------------------------------- */
/**
 * Parse une expression de filtre en AST, ou renvoie une erreur AMICALE (clé technique +
 * position). Texte vide/blanc ⇒ `{ ok:false, error:'empty' }`.
 *
 * Priorité : NON (préfixe) > ET > OU > SAUF (infixe « ET NON »). Adjacence = OU implicite.
 * `SAUF`/`NON` — voir la note d'en-tête du module — sont à la fois un préfixe de négation
 * unaire ET un infixe binaire « ET NON » de priorité la plus faible :
 *   `note>=4 SAUF type:vidéo` ⇒ `and[rating gte 4, not[type video]]`.
 * Lorsque l'opérande gauche du SAUF est déjà un `and` (venu d'un `ET`), les enfants sont
 * APLATIS (`a ET b SAUF c` ⇒ `and[a, b, not[c]]`) ; sinon un `and` enveloppe le tout
 * (`note>=4 OU (…) SAUF type:vidéo` ⇒ `and[ or[…], not[type video] ]`). Le membre DROIT
 * d'un SAUF est un `orExpr` complet (`a SAUF b OU c` ⇒ `and[a, not[or[b, c]]]`).
 */
export function parseFilterExpr(input) {
    if (input.trim() === '')
        return { ok: false, error: 'empty' };
    try {
        const tokens = classify(lex(input));
        if (tokens.length === 0)
            return { ok: false, error: 'empty' };
        const sourceLen = input.length;
        let i = 0;
        const peek = () => tokens[i];
        function parseExpr() {
            return parseExcept();
        }
        function parseExcept() {
            const left = parseOr();
            const nots = [];
            while (peek()?.t === 'except') {
                i++;
                nots.push({ kind: 'not', child: parseOr() });
            }
            if (nots.length === 0)
                return left;
            const base = left.kind === 'and' ? left.children : [left];
            return { kind: 'and', children: [...base, ...nots] };
        }
        function parseOr() {
            const first = parseAnd();
            const children = [first];
            for (;;) {
                const p = peek();
                if (p?.t === 'or') {
                    i++;
                    children.push(parseAnd());
                    continue;
                }
                // Adjacence (OU implicite) : un opérande démarre par une parenthèse ou une feuille.
                if (p !== undefined && (p.t === 'lparen' || p.t === 'leaf')) {
                    children.push(parseAnd());
                    continue;
                }
                break;
            }
            return children.length === 1 ? first : { kind: 'or', children };
        }
        function parseAnd() {
            const first = parseNot();
            const children = [first];
            while (peek()?.t === 'and') {
                i++;
                children.push(parseNot());
            }
            return children.length === 1 ? first : { kind: 'and', children };
        }
        function parseNot() {
            if (peek()?.t === 'except') {
                i++;
                return { kind: 'not', child: parseNot() };
            }
            return parseAtom();
        }
        function parseAtom() {
            const p = peek();
            if (p === undefined)
                throw new FilterParseError('unexpected_eof', sourceLen);
            if (p.t === 'lparen') {
                i++;
                const inner = parseExpr();
                const close = peek();
                if (close === undefined || close.t !== 'rparen')
                    throw new FilterParseError('unclosed_paren', p.pos);
                i++;
                return inner;
            }
            if (p.t === 'leaf') {
                i++;
                return p.node;
            }
            // Opérateur ou parenthèse fermante là où un opérande est attendu.
            throw new FilterParseError('unexpected_token', p.pos);
        }
        const ast = parseExpr();
        const rest = peek();
        if (rest !== undefined)
            throw new FilterParseError('unexpected_token', rest.pos);
        return { ok: true, ast };
    }
    catch (e) {
        if (e instanceof FilterParseError) {
            return e.pos === undefined
                ? { ok: false, error: e.code }
                : { ok: false, error: e.code, position: e.pos };
        }
        throw e;
    }
}
/* ------------------------------------------------------------------------- *
 * API publique : sérialisation canonique.
 * ------------------------------------------------------------------------- */
const OR_PREC = 1;
const AND_PREC = 2;
const NOT_PREC = 3;
const ATOM_PREC = 4;
/** Priorité d'un nœud (pour parenthéser au strict nécessaire). */
const precedence = (node) => {
    switch (node.kind) {
        case 'or':
            return OR_PREC;
        case 'and':
            return AND_PREC;
        case 'not':
            return NOT_PREC;
        default:
            return ATOM_PREC;
    }
};
/** Jetons FR canoniques des feuilles à énumération. */
const TYPE_CANON = {
    image: 'image',
    animatedImage: 'animée',
    video: 'vidéo',
    videoWithSound: 'vidéo-son',
};
const STATUS_CANON = {
    tagged: 'taguée',
    untagged: 'non-taguée',
    aiPending: 'à-valider',
};
const RATING_OP_CANON = { gte: '>=', lte: '<=', eq: '=' };
/**
 * Une valeur doit-elle être mise entre guillemets ? (espace, parenthèses, guillemet,
 * chaîne vide, ou un séparateur réservé `:`/`=`/`>=`/`<=` qui serait sinon relu comme un
 * préfixe `champ:`). NB : une valeur contenant elle-même un `"` n'est pas exprimable dans
 * ce mini-langage (le lexer traite `"` comme un délimiteur, sans échappement) — limitation
 * assumée pour un DSL de filtre.
 */
const needsQuote = (value) => {
    if (value === '')
        return true;
    if (/[\s()"]/.test(value))
        return true;
    if (value.includes(':') || value.includes('='))
        return true;
    if (value.includes('>=') || value.includes('<='))
        return true;
    return false;
};
const quote = (value) => `"${value}"`;
const maybeQuote = (value) => (needsQuote(value) ? quote(value) : value);
/** Un tag « mot nu » : brut si sûr et non réservé, sinon entre guillemets (`"et"`, `"nature morte"`). */
const serTagValue = (value) => needsQuote(value) || KEYWORDS.has(fold(value)) ? quote(value) : value;
/** Sérialise un enfant en le parenthésant si sa priorité est plus faible que le contexte. */
const wrapChild = (node, minPrec) => {
    const s = serializeNode(node);
    return precedence(node) < minPrec ? `(${s})` : s;
};
const serializeNode = (node) => {
    switch (node.kind) {
        case 'or':
            return node.children.map((c) => wrapChild(c, AND_PREC)).join(' OU ');
        case 'and':
            return node.children.map((c) => wrapChild(c, NOT_PREC)).join(' ET ');
        case 'not':
            return `NON ${wrapChild(node.child, ATOM_PREC)}`;
        case 'tag':
            return serTagValue(node.value);
        case 'type':
            return `type:${TYPE_CANON[node.value]}`;
        case 'rating':
            return `note${RATING_OP_CANON[node.op]}${node.value}`;
        case 'folder':
            return `dossier:${maybeQuote(node.value)}`;
        case 'filename':
            return `nom:${maybeQuote(node.value)}`;
        case 'status':
            return `statut:${STATUS_CANON[node.value]}`;
    }
};
/**
 * Sérialise un AST en TEXTE CANONIQUE qui RE-PARSE au même AST (round-trip). Jetons FR
 * canoniques (`ET`/`OU`/`NON`), guillemets ajoutés au besoin, parenthèses minimales mais
 * correctes selon la priorité. Un `and` issu d'un SAUF est rendu avec `ET`/`NON`
 * (`and[a, not[b]]` → `a ET NON b`) — sémantiquement et structurellement identique.
 */
export function filterAstToText(ast) {
    return serializeNode(ast);
}
//# sourceMappingURL=filter-expr.js.map